/**
 * rendi-sell-form orchestrator.
 *
 * Owns the answer state, sessionStorage persistence, step visibility, progress
 * bar, persistent estimate footer, Prosegui/Indietro navigation, per-step
 * validation and the final submit. The flow controller (Flow) and the estimate
 * calculator (Estimate) live here as pure, DOM-free helpers the element drives.
 */
if (!customElements.get('rendi-sell-form')) {
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // International phone syntax shared with intake.
  const PHONE_PATTERN = /^\+[1-9]\d{1,14}$/;
  // PRO-239 Experiment: the arm `required` makes the phone mandatory.
  const PHONE_FLAG = 'sell-form-phone-required';
  const PHONE_REQUIRED_ARM = 'required';
  // Keep in sync with min_quantity in sections/rendi-sell-form.liquid (the
  // rendered input's value and data-min).
  const MIN_QUANTITY = 10;
  // Declared in sections/rendi-sell-form.liquid → featured_*. Keep the typology
  // ids in sync with @rendi/intake-contracts featured-category.ts.
  const hostData = document.querySelector('rendi-sell-form')?.dataset || {};
  // On when the server rendered the step and the device clock is inside the
  // window: a page cached past the end stops promising a bonus intake drops.
  const FEATURED_ENABLED = Boolean(document.querySelector('rendi-sell-form [data-step="featured"]')) && (() => {
    const now = new Date();
    return now >= new Date(hostData.featuredStart || NaN) && now < new Date(hostData.featuredEnd || NaN);
  })();
  const FEATURED_FIELD = 'featured:';
  // The typology id of a featured field name, null for any other field.
  const featuredId = (name) => (name.startsWith(FEATURED_FIELD) ? name.slice(FEATURED_FIELD.length) : null);
  const featuredTotal = (counts) =>
    Object.values(counts || {}).reduce((sum, count) => sum + (Number(count) || 0), 0);
  const hasSelectedReturns = (state) =>
    Array.isArray(state.returns) && state.returns.length > 0;

  // The empty answer set. Single source of truth for both the initial state and
  // "Ricomincia" (handleRestart), so a new field can never drift between them.
  // `hasOrders` and `featuredEnabled` are per-request facts, re-asserted after
  // this by the caller; they default false here.
  const defaultAnswers = () => ({
    hasOrders: false,
    featuredEnabled: false,
    initialChoice: null,
    sellBridge: null,
    returnsBridge: null,
    brand: null,
    quantity: MIN_QUANTITY,
    featuredCounts: {},
    age: null,
    condition: null,
    washed: null,
    folded: null,
    firstName: '',
    email: '',
    phone: '',
    consent: false,
    termsAccepted: false,
    returns: []
  });

  /**
   * Flow: wizard navigation (pure, no DOM).
   *
   * Source of truth for navigation. Given the answers `state` (including the
   * `hasOrders` flag), it produces the ordered list of active steps, per-step
   * validity and whether the current step can proceed.
   *
   * Paths (every complete branch ends with trust -> details -> confirmation):
   *   - No returnable orders (guest):
   *       quantity -> brand -> age -> condition -> care -> trust
   *   - With orders, "returns" branch:
   *       initialChoice -> returns -> sellBridge
   *         sellBridge "no"  -> trust
   *         sellBridge "yes" -> quantity -> brand -> age -> condition -> care -> trust
   *   - With orders, "sell" branch:
   *       initialChoice -> quantity -> brand -> age -> condition -> care -> returnsBridge
   *         returnsBridge "no"  -> trust
   *         returnsBridge "yes" -> returns -> trust
   */
  const Flow = (function () {
    const SELL_STEPS = ['quantity', 'featured', 'brand', 'age', 'condition', 'care'];
    const FINAL_STEPS = ['trust', 'details', 'confirmation'];

    function isStepValid(step, state) {
      switch (step) {
        case 'initialChoice':
          return state.initialChoice === 'returns' || state.initialChoice === 'sell';
        case 'returns':
          return hasSelectedReturns(state);
        case 'sellBridge':
          return state.sellBridge === true || state.sellBridge === false;
        case 'returnsBridge':
          return state.returnsBridge === true || state.returnsBridge === false;
        case 'brand':
          return Boolean(state.brand);
        case 'quantity':
          return Number(state.quantity) >= MIN_QUANTITY;
        // Declaring none is an answer: most bags hold no jackets.
        case 'featured':
          return true;
        case 'age':
          return Boolean(state.age);
        case 'condition':
          return Boolean(state.condition);
        case 'care':
          return (
            state.washed !== null && state.washed !== undefined &&
            state.folded !== null && state.folded !== undefined
          );
        // The trust screen has no answer of its own: proceeding from it just
        // moves to the details step.
        case 'trust':
          return true;
        // `details` confirms (the host runs confirm() instead of advancing) and
        // `confirmation` is terminal, so neither gates via the proceed button.
        default:
          return false;
      }
    }

    // The three final screens (trust -> details -> confirmation) close every
    // complete branch. `confirmation` is only reached programmatically after a
    // successful submit, never via the proceed button.
    function getActiveSteps(state) {
      const sell = SELL_STEPS.filter((step) => step !== 'featured' || state.featuredEnabled);
      if (!state.hasOrders) {
        return [...sell, ...FINAL_STEPS];
      }

      if (state.initialChoice === 'returns') {
        const steps = ['initialChoice', 'returns', 'sellBridge'];
        if (state.sellBridge === true) {
          return [...steps, ...sell, ...FINAL_STEPS];
        }
        if (state.sellBridge === false) {
          return [...steps, ...FINAL_STEPS];
        }
        // Bridge not answered yet: the final steps aren't reachable until it is.
        return steps;
      }

      if (state.initialChoice === 'sell') {
        const sellSteps = ['initialChoice', ...sell, 'returnsBridge'];
        if (state.returnsBridge === true) {
          return [...sellSteps, 'returns', ...FINAL_STEPS];
        }
        if (state.returnsBridge === false) {
          return [...sellSteps, ...FINAL_STEPS];
        }
        // Bridge not answered yet: the final steps aren't reachable until it is.
        return sellSteps;
      }

      return ['initialChoice'];
    }

    // Progress bar model: the bar spans every step including the terminal
    // confirmation (which shows a full bar), the initial choice included.
    // Unanswered forks are assumed to take the shortest path (the returns
    // branch, no extra sell), so the total is defined from the first screen and
    // the short flow fills smoothly to full. Picking a longer option lengthens
    // the path, so the bar grows to fit the added steps (the fill steps back a
    // little, then climbs again).
    // `deterministic` is true when the path can't fork (no orders): only then
    // is the total fixed, so only then is a step counter meaningful.
    function getProgress(state, currentStep) {
      const assumed = {
        ...state,
        initialChoice: state.initialChoice ?? 'returns',
        sellBridge: state.sellBridge ?? false,
        returnsBridge: state.returnsBridge ?? false
      };
      const steps = getActiveSteps(assumed);
      return {
        current: steps.indexOf(currentStep) + 1,
        total: steps.length,
        deterministic: !state.hasOrders
      };
    }

    return {
      getActiveSteps,
      getProgress,
      isStepValid
    };
  })();

  /**
   * Estimate: credit calculator (pure, no DOM).
   *
   * Every function takes a plain `state` object and returns a number in integer
   * cents, the single money unit shared with the backend.
   * Coefficients, volume thresholds and the minimum quantity are unchanged from
   * the original single-page form.
   */
  const Estimate = (function () {
    const BRAND_VALUE = { low: 0.25, mid: 0.3, high: 0.8 };
    const AGE_VALUE = { 'last-year': 0.35, '2-3': 0.25, 'gt-4': 0 };
    const CONDITION_VALUE = { excellent: 0.25, good: 0.2, 'well-worn': 0.07 };

    function maxValue(map) {
      return Object.keys(map).reduce((max, key) => (map[key] > max ? map[key] : max), 0);
    }

    // Fallbacks used by the progressive footer for the steps the user hasn't
    // reached yet. Age and condition assume their most favourable answer.
    const AGE_BEST = maxValue(AGE_VALUE);
    const CONDITION_BEST = maxValue(CONDITION_VALUE);

    // Brand is the exception: `high` is worth almost three times any other tier,
    // so assuming it makes the preview collapse the moment a real brand is picked,
    // which reads as a loss mid-form. Assume the average tier instead, so the
    // preview can move either way once the question is answered.
    const BRAND_ASSUMED = BRAND_VALUE.mid;

    // Rendi Days: inside the campaign window the sell valuation is worth 50%
    // more. Returns cashback and the +5 € early-shipment bonus stay out of it.
    // The window is declared once in sections/rendi-sell-form.liquid and read off
    // the host element, so the dates and the deadline printed in the copy can't
    // drift apart. A missing or malformed date on either end is an Invalid Date,
    // so every comparison below is false and the promo stays off: half a removal
    // can't leave the promo running.
    // IMPORTANT: Keep in sync with the backend:
    // services/intake-service/src/capabilities/submission/domain/rendi-days.ts
    const RENDI_DAYS_MULTIPLIER = 1.5;
    const RENDI_DAYS_START = new Date(hostData.rendiDaysStart || NaN);
    const RENDI_DAYS_END_EXCLUSIVE = new Date(hostData.rendiDaysEnd || NaN);

    // Featured Category: a flat bonus per declared item, never promoted.
    const FEATURED_BONUS_CENTS = FEATURED_ENABLED
      ? Number(hostData.featuredBonusCentsPerItem) || 0
      : 0;

    // Decided here on the browser clock and in the backend on the server-stamped
    // received_at, so a submission at the very edge of the window can disagree.
    // The backend's answer is the one that pays: the form submits the bare
    // valuation and the promotion here is a display concern only.
    function isRendiDays() {
      const now = new Date();
      return now >= RENDI_DAYS_START && now < RENDI_DAYS_END_EXCLUSIVE;
    }

    // The campaign's last day, for the deadline printed in the promo copy. Held
    // in Rome time: the window is defined there, so the date shown must not shift
    // for a visitor in another timezone.
    // Empty when there is no window: Intl throws on an Invalid Date, and the
    // deadline is written into the copy unconditionally at connect time.
    function rendiDaysDeadline() {
      if (isNaN(RENDI_DAYS_END_EXCLUSIVE)) return '';
      const lastDay = new Date(RENDI_DAYS_END_EXCLUSIVE.getTime() - 1);
      return new Intl.DateTimeFormat(document.documentElement.lang || 'it', {
        day: 'numeric',
        month: 'long',
        timeZone: 'Europe/Rome'
      }).format(lastDay);
    }

    // The promotion, applied to a sell figure for display. Never on the way to
    // the payload: the server applies it to what it recalculates.
    function promote(sellCents) {
      return isRendiDays() ? Math.round(sellCents * RENDI_DAYS_MULTIPLIER) : sellCents;
    }

    function volumeBase(quantity) {
      if (quantity > 101) return 1.55;
      if (quantity > 60) return 1.7;
      return 2;
    }

    // True when the sell batch has every field needed for the full formula.
    // A missing brand is absent from BRAND_VALUE, so it never counts as complete.
    // This is only a predicate: it gates whether the sell contribution counts in the
    // final estimate and whether the payload carries `answers`. It computes nothing.
    function hasSellData(state) {
      return (
        Boolean(BRAND_VALUE[state.brand]) &&
        Number(state.quantity) >= MIN_QUANTITY &&
        Boolean(state.age) &&
        Boolean(state.condition) &&
        state.washed !== null && state.washed !== undefined &&
        state.folded !== null && state.folded !== undefined
      );
    }

    // Resolve one coefficient: the answered value, or the field's assumed value when
    // it is still unanswered. On the result step hasSellData guarantees every field is
    // answered, so the fallback only ever fires for the progressive footer.
    function fieldCoeff(map, key, fallback) {
      return key in map ? map[key] : fallback;
    }

    // `false` is an explicit "no" and scores 0; an unanswered field (null) assumes
    // the bonus, matching the footer's best-case figure.
    function boolBonus(value, bonus) {
      return value === false ? 0 : bonus;
    }

    // The single sell formula. Unanswered fields assume their fallback, so this is
    // the footer's progressive figure. On the result step every field is answered
    // (hasSellData gates it), so the same call yields the exact estimate: footer and
    // result never disagree once the section is complete.
    function sellValue(state) {
      const quantity = Number(state.quantity);
      const price = fieldCoeff(BRAND_VALUE, state.brand, BRAND_ASSUMED)
        + fieldCoeff(AGE_VALUE, state.age, AGE_BEST)
        + fieldCoeff(CONDITION_VALUE, state.condition, CONDITION_BEST)
        + boolBonus(state.washed, 0.05)
        + boolBonus(state.folded, 0.1);
      // Round once at cent precision (mirrors the backend's calculateSellCredit)
      // so the submitted sell_cents matches the recalculation exactly.
      return Math.round(volumeBase(quantity) * price * quantity * 100);
    }

    function featuredBonus(state) {
      return featuredTotal(state.featuredCounts) * FEATURED_BONUS_CENTS;
    }

    function calculateSellEstimate(state) {
      if (!hasSellData(state)) return 0;
      return sellValue(state);
    }

    function calculateReturnsCashback(state) {
      if (!hasSelectedReturns(state)) return 0;
      // item.cashback is already integer cents (Shopify final_line_price × 0.25,
      // rounded in Liquid); sum directly with no euro round-trip.
      return state.returns.reduce((sum, item) => sum + (item.cashback || 0), 0);
    }

    function calculateEstimate(state) {
      return calculateSellEstimate(state) + calculateReturnsCashback(state);
    }

    // Progressive estimate for the persistent footer. The sell contribution shows
    // up as soon as a valid quantity is known — brand and the quality steps don't
    // gate it. Age and condition are assumed at their best case until answered, so
    // they only ever adjust the value down; brand is assumed average, so answering it
    // can move the value either way. Selected returns always count, since their
    // cashback is exact, so the returns-only path still shows a value; otherwise the
    // footer shows "-".
    function calculateProgressive(state) {
      const sellReady = Number(state.quantity) >= MIN_QUANTITY;
      const returnsCashback = calculateReturnsCashback(state);

      if (!sellReady && returnsCashback === 0) {
        return { calculable: false, promo: false, value: 0, display: 0 };
      }

      // `value` is the bare figure, `display` the promoted one: Rendi Days lifts
      // the sell half only and the returns cashback passes through. `promo` says
      // whether the two differ, which is what the promo surfaces key off.
      const sell = sellReady ? sellValue(state) : 0;
      const bonus = sellReady ? featuredBonus(state) : 0;
      return {
        calculable: true,
        promo: isRendiDays() && sell > 0,
        value: sell + bonus + returnsCashback,
        display: promote(sell) + bonus + returnsCashback
      };
    }

    return {
      promote,
      rendiDaysDeadline,
      featuredBonus,
      FEATURED_BONUS_CENTS,
      calculateSellEstimate,
      calculateReturnsCashback,
      calculateEstimate,
      calculateProgressive,
      hasSellData
    };
  })();

  class RendiSellForm extends HTMLElement {
    constructor() {
      super();

      this.schemaVersion = '2026-07-13';
      this.state = defaultAnswers();
      this.currentStepIndex = 0;
      this.isSubmitting = false;
      this.submitState = 'idle';
      this.submitMessage = '';
      this.hasAttemptedSubmit = false;
      this.phoneRefused = false;
      // The pickup-terms accept CTA stays locked until the drawer body has been
      // scrolled to the end (or the content doesn't overflow). Per-open, never
      // persisted.
      this.termsScrolledToEnd = false;

      this.handleFieldChangeBound = this.handleFieldChange.bind(this);
      this.handleSubmitBound = this.handleFormSubmit.bind(this);
      this.handleNextBound = this.handleNext.bind(this);
      this.handleBackBound = this.handleBack.bind(this);
      this.handlePopStateBound = this.handlePopState.bind(this);
      this.handleRestartBound = this.handleRestart.bind(this);
      this.handleTermsAcceptBound = this.handleTermsAccept.bind(this);
      this.handleTermsScrollBound = this.handleTermsScroll.bind(this);
    }

    connectedCallback() {
      // E2E override: tests inject window.__RENDI_SUBMIT_ENDPOINT__ before page scripts run
      this.endpoint = window.__RENDI_SUBMIT_ENDPOINT__ || this.dataset.endpoint || '';
      this.storageKey = this.dataset.storageKey || '';
      this.privacyUrl = this.dataset.privacyUrl || '';
      const customerEl = this.querySelector('script[data-customer]');
      const customer = customerEl ? JSON.parse(customerEl.textContent) : {};
      this.customerEmail = customer.email || '';
      this.customerFirstName = customer.firstName || '';
      this.country = (this.dataset.country || '').toUpperCase().slice(0, 2);
      const hasOrders = this.hasAttribute('data-has-orders');

      const msgEl = this.querySelector('script[data-messages]');
      this.messages = msgEl ? JSON.parse(msgEl.textContent) : {};

      this.form = this.querySelector('form');

      this.stepContainers = new Map();
      this.querySelectorAll('[data-step]').forEach((el) => {
        this.stepContainers.set(el.dataset.step, el);
      });

      this.progressBar = this.querySelector('rendi-progress-bar');
      // The back arrow lives in the layout header (next to the logo), outside
      // this element, so it's looked up on the document rather than scoped here.
      this.backButton = document.querySelector('[data-wizard-back]');
      this.footer = this.querySelector('rendi-wizard-footer');

      this.resultPanel = this.querySelector('rendi-result-panel');

      // Trust step: a dumb panel painting the estimate card + answers recap,
      // driven through setData(). It emits `rendi:restart` (handled below).
      this.trustPanel = this.querySelector('rendi-trust-panel');

      // Details and confirmation estimate echoes.
      this.detailsEstimateNode = this.querySelector('[data-details-estimate]');
      this.confirmationEstimateNode = this.querySelector('[data-confirmation-estimate]');
      this.featuredBox = this.querySelector('[data-featured-box]');
      this.featuredNote = this.querySelector('[data-featured-note]');
      this.featuredQuantityNode = this.querySelector('[data-featured-quantity]');
      this.featuredSummary = this.querySelector('[data-featured-summary]');

      // Rendi Days: every promo-only node shows and hides together, and the
      // struck figures are repainted together (see renderRendiDays).
      this.rendiDaysNodes = Array.from(this.querySelectorAll('[data-rendi-days]'));
      this.rendiDaysBaseNodes = Array.from(this.querySelectorAll('[data-rendi-days-base]'));
      this.rendiDaysFooterNote = this.querySelector('[data-rendi-days-footer-note]');
      this.trustTitleNode = this.querySelector('.rendi-trust-card__label');
      // The deadline never changes, so the copy gets it once instead of per render.
      this.querySelectorAll('[data-rendi-days-deadline]').forEach((node) => {
        node.textContent = Estimate.rendiDaysDeadline();
      });

      // The pickup-terms drawer: the accept CTA inside it triggers the POST,
      // and the submit status while the request is in flight lives there too.
      this.termsSheet = this.querySelector('rendi-bottom-sheet[data-sheet="terms"]');
      this.termsButton = this.termsSheet?.querySelector('[data-terms-accept]');
      this.termsStatus = this.termsSheet?.querySelector('[data-terms-status]');
      // Scroll container gating the accept CTA (see termsScrolledToEnd).
      this.termsBody = this.termsSheet?.querySelector('.rendi-sheet__body');

      this.choiceGroups = Array.from(this.querySelectorAll('rendi-choice-group'));
      this.quantityInput = this.querySelector('rendi-quantity-input[data-name="quantity"]');
      this.featuredInputs = Array.from(this.querySelectorAll(`rendi-quantity-input[data-name^="${FEATURED_FIELD}"]`));
      this.orderReturns = this.querySelector('rendi-order-returns');

      this.showCampaignBanners();
      this.setupListeners();
      this.restoreState();
      // Served inside the window but opened past it: no banner, no stored counts.
      if (!FEATURED_ENABLED) {
        this.stepContainers.get('quantity')?.querySelector('.rendi-alert--sky')?.remove();
        this.state.featuredCounts = {};
      }
      this.syncFeaturedCounts({ drop: true });
      // hasOrders is a per-request server fact, not an answer: re-assert it after
      // restoring so a stale stored value never drives the flow. Without orders the
      // returns branch isn't rendered, so its stored answers (returns + the branch
      // forks) must never survive into the estimate or the payload — they would leak
      // a prior session's order data into a guest submission.
      this.state.hasOrders = hasOrders;
      this.state.featuredEnabled = FEATURED_ENABLED;
      if (!hasOrders) {
        this.state.returns = [];
        this.state.initialChoice = null;
        this.state.sellBridge = null;
        this.state.returnsBridge = null;
      }
      this.render();
      // Baseline history entry for the first step: a back from a later step pops
      // here, and a back from here leaves the wizard. Track the entry view
      // directly, without the scroll/focus afterStepChange would trigger on load.
      window.history.replaceState({ ...window.history.state, rendiStep: this.currentStepIndex }, '');
      this.trackStepView(this.getCurrentStep());
    }

    // Decided on the device clock: the page may come from Shopify's cache, so the
    // server can't. Per step, the first open window in list order wins; a
    // malformed date compares false and keeps its banner hidden.
    showCampaignBanners() {
      const now = new Date();
      const filledSteps = new Set();
      this.querySelectorAll('[data-campaign-banner]').forEach((banner) => {
        const step = banner.closest('[data-step]');
        if (filledSteps.has(step)) return;
        if (now >= new Date(banner.dataset.start) && now < new Date(banner.dataset.end)) {
          banner.hidden = false;
          filledSteps.add(step);
        }
      });
    }

    setupListeners() {
      // The result panel re-emits its email/consent inputs as
      // rendi:field-change, so a single field-change listener feeds every field.
      this.addEventListener('rendi:field-change', this.handleFieldChangeBound);
      this.form?.addEventListener('submit', this.handleSubmitBound);
      this.addEventListener('rendi:proceed', this.handleNextBound);
      this.backButton?.addEventListener('click', this.handleBackBound);
      window.addEventListener('popstate', this.handlePopStateBound);
      this.addEventListener('rendi:restart', this.handleRestartBound);
      this.termsButton?.addEventListener('click', this.handleTermsAcceptBound);
      this.termsBody?.addEventListener('scroll', this.handleTermsScrollBound, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener('rendi:field-change', this.handleFieldChangeBound);
      this.form?.removeEventListener('submit', this.handleSubmitBound);
      this.removeEventListener('rendi:proceed', this.handleNextBound);
      this.backButton?.removeEventListener('click', this.handleBackBound);
      window.removeEventListener('popstate', this.handlePopStateBound);
      this.removeEventListener('rendi:restart', this.handleRestartBound);
      this.termsButton?.removeEventListener('click', this.handleTermsAcceptBound);
      this.termsBody?.removeEventListener('scroll', this.handleTermsScrollBound);
    }

    /* ── Navigation ── */

    getSteps() {
      return Flow.getActiveSteps(this.state);
    }

    getCurrentStep() {
      return this.getSteps()[this.currentStepIndex];
    }

    // Single entry point for every step transition. Forward moves push a new
    // history entry; popstate replays a move with `push: false` because the
    // entry it lands on already exists in the browser stack.
    navigateTo(index, { push = false } = {}) {
      const steps = this.getSteps();
      const target = Math.max(0, Math.min(index, steps.length - 1));

      // Leaving the quantity step settles the bag: only then can a smaller bag
      // drop the featured declaration, never on a value typed halfway.
      if (steps[this.currentStepIndex] === 'quantity') this.syncFeaturedCounts({ drop: true });
      this.currentStepIndex = target;
      if (push) {
        window.history.pushState({ ...window.history.state, rendiStep: target }, '');
      }
      this.render();
      this.afterStepChange();
    }

    handleNext() {
      const steps = this.getSteps();
      const current = steps[this.currentStepIndex];

      // On the details step the footer button confirms instead of advancing;
      // reaching `confirmation` is driven by a successful submit.
      if (current === 'details') {
        this.confirm();
        return;
      }

      if (!Flow.isStepValid(current, this.state)) return;
      if (this.currentStepIndex >= steps.length - 1) return;

      this.navigateTo(this.currentStepIndex + 1, { push: true });
    }

    // The in-page back arrow and the device back button must behave the same,
    // so the arrow defers to the browser: history.back() pops an entry and
    // handlePopState drives the actual step change.
    handleBack() {
      if (this.currentStepIndex <= 0) return;
      window.history.back();
    }

    handlePopState(event) {
      const target = event.state?.rendiStep;
      // No rendiStep means we popped past the baseline (out of the wizard):
      // let the browser navigate away.
      if (typeof target !== 'number') return;
      this.navigateTo(target);
    }

    afterStepChange() {
      const currentStep = this.getCurrentStep();
      const container = this.stepContainers.get(currentStep);
      if (!container) return;

      this.trackStepView(currentStep);

      window.requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const title = container.querySelector('[data-step-title]');
        if (title && typeof title.focus === 'function') {
          title.focus({ preventScroll: true });
        }
      });
    }

    // Funnel primitive: one event per actual step view, back/forward included.
    // Funnel tools dedupe per user at query time, so re-views never inflate the
    // funnel. The trust view carries the estimate and answer-derived flow that
    // the old rendi_estimate_shown event used to send.
    trackStepView(step) {
      const data = {
        step_name: step,
        step_index: this.getSteps().indexOf(step) + 1,
        flow: this.state.hasOrders ? 'with_orders' : 'sell_only'
      };
      if (step === 'trust') {
        data.estimate = this.estimateValueEuros();
        data.flow_detail = this.getFlowKey();
      }
      this.trackEvent('rendi_step_view', data);
    }

    /* ── Field handling ── */

    clearChoice(name) {
      this.state[name] = null;
      this.choiceGroups.find((group) => group.dataset.name === name)?.clear({ silent: true });
    }

    clearSellState() {
      ['brand', 'age', 'condition', 'washed', 'folded'].forEach((name) => {
        this.clearChoice(name);
      });
      this.state.quantity = null;
      this.quantityInput?.setValue(null, { silent: true });
      this.syncFeaturedCounts({ drop: true });
    }

    // The featured items are part of the bag: a settled bag (`drop`) shrunk
    // below them drops the whole declaration, and each stepper stops where the
    // shared room runs out. The input being typed in (`source`) keeps its text.
    syncFeaturedCounts({ drop = false, source = null } = {}) {
      const quantity = Number(this.state.quantity) || 0;
      if (drop && featuredTotal(this.state.featuredCounts) > quantity) this.state.featuredCounts = {};
      const room = Math.max(0, quantity - featuredTotal(this.state.featuredCounts));
      this.featuredInputs.forEach((input) => {
        const count = Number(this.state.featuredCounts[featuredId(input.name)]) || 0;
        input.max = count + room;
        if (input.name !== source) input.setValue(count, { silent: true });
      });
    }

    clearReturnsState() {
      this.state.returns = [];
      this.orderReturns?.clear({ silent: true });
    }

    handleFieldChange(event) {
      const { name, value } = event.detail;
      const typology = featuredId(name);
      if (typology) {
        this.state.featuredCounts = { ...this.state.featuredCounts, [typology]: value };
      } else {
        this.state[name] = value;
      }
      if (name === 'phone') this.phoneRefused = false;
      if (name === 'quantity' || typology) this.syncFeaturedCounts({ source: name });

      if (name === 'initialChoice') {
        this.clearChoice('sellBridge');
        this.clearChoice('returnsBridge');
        if (value === 'sell') this.clearReturnsState();
        if (value === 'returns') this.clearSellState();
      } else if (name === 'sellBridge' && value === false) {
        this.clearSellState();
      } else if (name === 'returnsBridge' && value === false) {
        this.clearReturnsState();
      }

      this.submitState = 'idle';
      this.submitMessage = '';
      this.storeState();
      this.render();
    }

    handleFormSubmit(event) {
      event.preventDefault();
      // Enter in a details field behaves like the Conferma button; confirm()
      // ignores the implicit submission on any other step.
      this.confirm();
    }

    // "Ricomincia" on the trust step: wipe every answer (hasOrders and
    // featuredEnabled are per-request facts and survive) and go back to the
    // first step, no confirmation asked.
    handleRestart() {
      const stepsBackToStart = this.currentStepIndex;
      this.state = { ...defaultAnswers(), hasOrders: this.state.hasOrders, featuredEnabled: this.state.featuredEnabled };
      this.choiceGroups.forEach((group) => group.clear({ silent: true }));
      this.quantityInput?.setValue(MIN_QUANTITY, { silent: true });
      this.syncFeaturedCounts();
      this.orderReturns?.clear({ silent: true });
      this.resultPanel?.setValues({ firstName: '', email: '', phone: '', consent: false });
      this.resetTerms();
      this.trustPanel?.reset();
      this.submitState = 'idle';
      this.submitMessage = '';
      this.hasAttemptedSubmit = false;
      this.phoneRefused = false;
      this.clearStoredState();
      // Return to the original step-0 history entry. Pushing or replacing only
      // the current entry would leave the old wizard steps behind it, so browser
      // Back would replay stale indexes after the answers were wiped.
      if (stepsBackToStart > 0) {
        window.history.go(-stepsBackToStart);
      } else {
        this.render();
        this.afterStepChange();
      }
    }

    // Re-arm the pickup-terms acceptance: accepting the terms is a per-attempt
    // act, never a persisted flag.
    resetTerms() {
      this.state.termsAccepted = false;
    }

    // Tapping the accept CTA inside the pickup-terms drawer is the actual
    // submit trigger.
    handleTermsAccept() {
      this.state.termsAccepted = true;
      this.submit();
    }

    // Unlock the accept CTA once the drawer body reaches its end. Latched: once
    // read, scrolling back up doesn't re-lock. 2px slack absorbs sub-pixel
    // rounding on the scroll metrics.
    handleTermsScroll() {
      if (this.termsScrolledToEnd || !this.termsBody) return;
      const body = this.termsBody;
      if (body.scrollTop + body.clientHeight < body.scrollHeight - 2) return;
      this.termsScrolledToEnd = true;
      this.renderTermsSheet(Boolean(this.termsSheet) && !this.termsSheet.hidden);
    }

    // On open, decide whether the read-gate applies: content that fits without
    // scrolling has nothing to read to the end, so unlock immediately and never
    // trap the user. Measured next frame, once open() has cleared `hidden`.
    // ponytail: no resize re-check; rotating the device mid-drawer from
    // overflowing to non-overflowing is the one uncovered corner.
    syncTermsScrollGate() {
      requestAnimationFrame(() => {
        const body = this.termsBody;
        this.termsScrolledToEnd = !body || body.scrollHeight <= body.clientHeight + 2;
        this.renderTermsSheet(Boolean(this.termsSheet) && !this.termsSheet.hidden);
      });
    }

    /* ── Versioned persistence (answers only; wizard always restarts at step 0) ── */

    restoreState() {
      const storedState = this.readStoredState();
      if (!storedState) return;

      this.state = { ...this.state, ...storedState };

      this.choiceGroups.forEach((group) => {
        const value = this.state[group.dataset.name];
        if (value === null || value === undefined || value === '') return;
        group.setValue(value, { silent: true });
      });

      if (this.quantityInput) {
        this.quantityInput.setValue(this.state.quantity, { silent: true });
        this.state.quantity = this.quantityInput.getValue();
      }

      if (this.orderReturns && Array.isArray(this.state.returns) && this.state.returns.length > 0) {
        this.orderReturns.setSelected(this.state.returns, { silent: true });
        // Drop any stored items no longer present in the rendered orders.
        this.state.returns = this.orderReturns.getSelectedItems();
      }

      // termsAccepted deliberately never restores: the pickup-terms drawer must
      // be re-confirmed on every visit.
      this.state.termsAccepted = false;
      this.resultPanel?.setValues({
        firstName: this.state.firstName || '',
        email: this.state.email || '',
        phone: this.state.phone || '',
        consent: Boolean(this.state.consent)
      });
    }

    storeState() {
      if (!this.storageKey) return;
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify({
          schemaVersion: this.schemaVersion,
          state: this.state
        }));
      } catch {
        // Ignore storage quota and availability errors.
      }
    }

    clearStoredState() {
      if (!this.storageKey) return;
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch {
        // Ignore storage availability errors.
      }
    }

    readStoredState() {
      if (!this.storageKey) return null;
      try {
        const rawValue = window.sessionStorage.getItem(this.storageKey);
        if (!rawValue) return null;

        const storedValue = JSON.parse(rawValue);
        const hasValidState = storedValue?.state
          && typeof storedValue.state === 'object'
          && !Array.isArray(storedValue.state);

        if (storedValue?.schemaVersion !== this.schemaVersion || !hasValidState) {
          this.clearStoredState();
          return null;
        }

        return storedValue.state;
      } catch {
        this.clearStoredState();
        return null;
      }
    }

    /* ── Estimate / validation helpers (delegate to Estimate) ── */

    hasReturnsSelected() {
      return hasSelectedReturns(this.state);
    }

    hasSellDataComplete() {
      return Estimate.hasSellData(this.state);
    }

    canShowResult() {
      return this.hasSellDataComplete() || this.hasReturnsSelected();
    }

    calculateSellEstimate() {
      return Estimate.calculateSellEstimate(this.state);
    }

    calculateReturnsCashback() {
      return Estimate.calculateReturnsCashback(this.state);
    }

    calculateEstimate() {
      return Estimate.calculateEstimate(this.state);
    }

    // What the screen shows. The calculate* methods stay bare because that is
    // what the payload carries; every rendered figure goes through these two, so
    // the Rendi Days promotion never reaches the wire and never misses a surface.
    displaySellEstimate() {
      return Estimate.promote(this.calculateSellEstimate());
    }

    // Shown on top of the sell figure, never submitted: intake adds it itself.
    featuredBonus() {
      return this.hasSellDataComplete() ? Estimate.featuredBonus(this.state) : 0;
    }

    displayEstimate() {
      return this.displaySellEstimate() + this.featuredBonus() + this.calculateReturnsCashback();
    }

    // The estimate in euros, for analytics only (Meta/GA report value in euros;
    // the estimate is integer cents internally). The promoted figure, matching
    // what the server reports to Meta for the same submission.
    estimateValueEuros() {
      return this.displayEstimate() / 100;
    }

    // The one place the logged-in customer's account email backstops the email
    // field (hidden for logged-in customers): validation and the payload both
    // resolve through here, so the state never needs a pre-filled default.
    getResolvedEmail() {
      return String(this.state.email || this.customerEmail || '').trim();
    }

    // Same backstop pattern as the email: the field is hidden only when the
    // logged-in customer already has a first name on the account, which then
    // backstops the payload's required contact.first_name.
    getResolvedFirstName() {
      return String(this.state.firstName || this.customerFirstName || '').trim();
    }

    isValidFirstName() {
      const name = this.getResolvedFirstName();
      // Mirrors the intake schema: trimmed, 1-100 chars.
      return name.length > 0 && name.length <= 100;
    }

    isConsentSatisfied() {
      return !this.resultPanel?.hasConsent() || Boolean(this.state.consent);
    }

    // The pickup-terms drawer confirms the sell-specific rules (excluded
    // brands, original items, credit usage), so it only applies when the batch
    // carries a sell estimate — the returns-only path skips it and POSTs
    // directly from the Conferma button.
    termsRequired() {
      return Boolean(this.termsButton) && this.hasSellDataComplete();
    }

    isValidEmail() {
      return EMAIL_PATTERN.test(this.getResolvedEmail());
    }

    // Normalize the typed phone to E.164: strip separators, turn the 00 prefix
    // into +, and assume Italy (+39) for national-format numbers only when the
    // active market is Italy. Other markets must provide an international prefix.
    getNormalizedPhone() {
      let phone = String(this.state.phone || '').replace(/[\s().\/-]/g, '');
      if (!phone) return '';
      if (phone.startsWith('00')) phone = `+${phone.slice(2)}`;
      if (!phone.startsWith('+') && this.country === 'IT') phone = `+39${phone}`;
      return phone;
    }

    // 'missing' | 'invalid' | null. Empty is valid unless the Experiment arm
    // requires the phone; anything typed must normalize to E.164 (the backend
    // rejects the whole submission otherwise).
    phoneError() {
      const phone = this.getNormalizedPhone();
      if (phone === '') return this.isPhoneRequired() ? 'missing' : null;
      return PHONE_PATTERN.test(phone) ? null : 'invalid';
    }

    // A returns-only request never takes part, so it never reads the arm. The
    // first read (the Exposure) is the first details render.
    isPhoneRequired() {
      return this.getFlowKey() !== 'order-returns' && window.rendiExperimentArm?.(PHONE_FLAG) === PHONE_REQUIRED_ARM;
    }

    // Accepts integer cents (the estimate's internal unit) and renders euros.
    formatEstimate(cents) {
      return new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format((cents || 0) / 100);
    }

    /* ── Render ── */

    render() {
      const steps = this.getSteps();
      this.currentStepIndex = Math.max(0, Math.min(this.currentStepIndex, steps.length - 1));
      const currentStep = steps[this.currentStepIndex];

      this.stepContainers.forEach((el, name) => {
        el.hidden = name !== currentStep;
      });

      // Resolved once and passed down: the footer and the promo surfaces paint
      // the same progressive figure.
      const progressive = Estimate.calculateProgressive(this.state);
      this.renderProgress(currentStep);
      this.renderFooter(currentStep, progressive);
      // On its own step the bonus caption replaces the footer's Rendi Days note.
      const featuredNoteBonus = currentStep === 'featured' ? Estimate.featuredBonus(this.state) : 0;
      this.renderRendiDays(progressive, featuredNoteBonus > 0);
      this.renderFeatured(featuredNoteBonus);

      if (currentStep === 'trust') this.renderTrustStep();
      if (currentStep === 'details') this.renderDetailsStep();
      if (currentStep === 'confirmation') this.renderConfirmationStep();
      this.renderSubmitStatus();
    }

    renderProgress(currentStep) {
      // The terminal confirmation keeps the (full) bar but disables going back:
      // the submit already happened, there is nothing to change behind it.
      this.setBackVisible(currentStep !== 'confirmation' && this.currentStepIndex > 0);

      if (!this.progressBar) return;

      const progress = Flow.getProgress(this.state, currentStep);
      this.progressBar.hidden = false;
      this.progressBar.setProgress({
        current: progress.current,
        total: progress.total,
        showCount: progress.deterministic
      });
    }

    setBackVisible(visible) {
      if (!this.backButton) return;
      this.backButton.disabled = !visible;
      this.backButton.classList.toggle('is-hidden', !visible);
    }

    renderFooter(currentStep, progressive) {
      if (!this.footer) return;

      const mode = currentStep === 'trust' || currentStep === 'details' || currentStep === 'confirmation'
        ? currentStep
        : 'question';

      this.footer.hidden = false;
      this.footer.setMode(mode);

      this.footer.setEstimate(progressive.calculable
        ? `${this.formatEstimate(progressive.display)} €`
        : '-');
      if (mode === 'confirmation') {
        this.footer.setLabel(this.messages.creditTotalLabel);
        return;
      }
      if (mode === 'details') {
        this.footer.setLabel(this.messages.creditTotalLabel);
        this.footer.setProceed({
          visible: true,
          enabled: !this.isSubmitting,
          label: this.isSubmitting ? this.messages.submitting : this.messages.confirm
        });
      } else {
        this.footer.setLabel(this.messages.estimateLabel);
        this.footer.setProceed({
          visible: true,
          enabled: Flow.isStepValid(currentStep, this.state),
          // Rendi Days: the trust step already carries the promo urgency, so its
          // CTA confirms instead of just proceeding.
          label: mode === 'trust' && progressive.promo ? this.messages.confirm : this.messages.proceed
        });
      }
    }

    // The four Rendi Days surfaces (footer, trust card, details strip,
    // confirmation) show the same thing: the pre-campaign figure struck through
    // and the deadline note, so one pass paints them all. The promoted figure is
    // whatever each surface already renders through display*Estimate().
    renderRendiDays(progressive, featuredNoteShown) {
      this.rendiDaysNodes.forEach((node) => {
        node.hidden = !progressive.promo || (featuredNoteShown && node === this.rendiDaysFooterNote);
      });
      // The trust card headline is the one piece the promo replaces rather than
      // adds, so it swaps through the messages like every other runtime label.
      // Written only when it actually changes: the card is aria-live, so an
      // identical rewrite would make a screen reader re-announce it. An empty
      // message (no data-messages) leaves the copy Liquid rendered.
      if (this.trustTitleNode) {
        const title = progressive.promo
          ? this.messages.rendiDaysCreditTitle
          : this.messages.creditTitle;
        if (title && this.trustTitleNode.textContent !== title) {
          this.trustTitleNode.textContent = title;
        }
      }
      if (!progressive.promo) return;

      // The struck figure is the bare valuation, which is what the calculators
      // return: nothing to divide back out.
      this.rendiDaysBaseNodes.forEach((node) => {
        node.textContent = `${this.formatEstimate(progressive.value)} €`;
      });
    }

    renderFeatured(noteBonus) {
      if (!this.featuredNote) return;
      const quantity = Number(this.state.quantity) || 0;
      this.featuredQuantityNode.textContent = String(quantity);
      this.featuredInputs.forEach((input) => {
        const count = Number(this.state.featuredCounts[featuredId(input.name)]) || 0;
        const node = input.querySelector('[data-featured-row-bonus]');
        node.hidden = count === 0;
        node.textContent = this.messages.featured.rowBonus.replace('__AMOUNT__', this.formatEstimate(count * Estimate.FEATURED_BONUS_CENTS));
      });
      this.featuredSummary.textContent = this.plural(this.messages.featured.summary, featuredTotal(this.state.featuredCounts))
        .replace('__QUANTITY__', quantity);
      this.featuredNote.hidden = noteBonus === 0;
      if (noteBonus === 0) return;
      this.featuredNote.querySelector('[data-featured-note-amount]').textContent = this.formatEstimate(noteBonus);
    }

    // "2 invernali, 1 primaverile" off the per-typology plural pairs in
    // data-messages, which Liquid writes in typology order.
    featuredRecap() {
      const labels = this.messages.featured?.recap || {};
      return Object.entries(labels)
        .map(([id, forms]) => [forms, Number(this.state.featuredCounts[id]) || 0])
        .filter(([, count]) => count > 0)
        .map(([forms, count]) => this.plural(forms, count))
        .join(', ');
    }

    plural(forms, count) {
      return (count === 1 ? forms.one : forms.other).replace('__COUNT__', count);
    }

    // The host resolves the estimate + the recap display labels (value -> label
    // maps come from data-messages); the panel only paints them. A recap row
    // whose value is '' stays hidden.
    renderTrustStep() {
      if (!this.trustPanel) return;
      const hasSell = this.hasSellDataComplete();
      const hasReturns = this.hasReturnsSelected();
      const recapMsgs = this.messages.recap || {};
      const boolLabel = (value) => (value ? recapMsgs.yes : recapMsgs.no) || '';

      this.trustPanel.setData({
        total: this.formatEstimate(this.displayEstimate()),
        // The bonus is part of the sale, so sell + returns adds up to the total.
        sell: this.formatEstimate(this.displaySellEstimate() + this.featuredBonus()),
        returns: this.formatEstimate(this.calculateReturnsCashback()),
        showBreakdown: hasSell && hasReturns,
        rows: {
          quantity: hasSell ? String(Number(this.state.quantity)) : '',
          brand: hasSell ? recapMsgs.brand?.[this.state.brand] || '' : '',
          age: hasSell ? recapMsgs.age?.[this.state.age] || '' : '',
          condition: hasSell ? recapMsgs.condition?.[this.state.condition] || '' : '',
          washed: hasSell ? boolLabel(this.state.washed) : '',
          folded: hasSell ? boolLabel(this.state.folded) : '',
          featured: hasSell ? this.featuredRecap() : '',
          returns: hasReturns ? String(this.state.returns.length) : ''
        }
      });
      if (this.featuredBox) {
        const bonus = this.featuredBonus();
        this.featuredBox.hidden = bonus === 0;
        this.featuredBox.querySelector('[data-featured-box-amount]').textContent = this.formatEstimate(bonus);
        this.featuredBox.querySelector('[data-featured-box-body]').textContent =
          this.plural(this.messages.featured.boxBody, featuredTotal(this.state.featuredCounts));
      }
    }

    renderDetailsStep() {
      const panel = this.resultPanel;

      if (this.detailsEstimateNode) {
        this.detailsEstimateNode.textContent = this.formatEstimate(this.displayEstimate());
      }

      if (!panel) return;
      panel.setPhoneRequired(this.isPhoneRequired());
      let phoneError = null;
      if (this.phoneRefused) phoneError = 'invalid';
      else if (this.hasAttemptedSubmit) phoneError = this.phoneError();
      panel.showErrors({
        firstName: panel.hasFirstName() && this.hasAttemptedSubmit && !this.isValidFirstName(),
        email: panel.hasEmail() && this.hasAttemptedSubmit && !this.isValidEmail(),
        phone: panel.hasPhone() ? phoneError : null,
        consent: panel.hasConsent() && this.hasAttemptedSubmit && !this.isConsentSatisfied()
      });
    }

    renderConfirmationStep() {
      const hasPhone = Boolean(this.getNormalizedPhone());
      const email = this.querySelector('[data-confirmation-email]');
      const sms = this.querySelector('[data-confirmation-sms]');
      if (email) email.hidden = hasPhone;
      if (sms) sms.hidden = !hasPhone;
      const estimate = this.displayEstimate();
      if (this.confirmationEstimateNode) {
        this.confirmationEstimateNode.textContent = this.formatEstimate(estimate);
      }
      // Bonus banner: base = confirmation estimate, total = base + fixed 5 € bonus.
      const baseNode = this.querySelector('[data-bonus-base]');
      const totalNode = this.querySelector('[data-bonus-total]');
      const deadlineNode = this.querySelector('[data-bonus-deadline]');
      if (baseNode) baseNode.textContent = `${this.formatEstimate(estimate)} €`;
      if (totalNode) totalNode.textContent = `${this.formatEstimate(estimate + 500)} €`;
      if (deadlineNode) deadlineNode.textContent = this.shipByDeadline();
    }

    // Ship-by deadline for the +5 € bonus: 7 business days from now, formatted
    // in the storefront locale (e.g. "4 agosto"). Weekends don't count.
    shipByDeadline() {
      const date = new Date();
      let added = 0;
      while (added < 7) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      const locale = document.documentElement.lang || 'it';
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
    }

    // Submit status has a single owner at a time: the pickup-terms drawer while
    // it gates the POST, the inline details panel otherwise. The drawer only
    // opens (programmatically, from confirm()) once it owns the submit, so its
    // open state is the owner — keyed on termsRequired() alone the pre-open
    // errors from confirm() would land in the still-closed, invisible drawer.
    renderSubmitStatus() {
      const inDrawer = Boolean(this.termsSheet) && !this.termsSheet.hidden;
      this.resultPanel?.setStatus({
        message: inDrawer ? '' : this.submitMessage,
        state: inDrawer ? 'idle' : this.submitState
      });
      this.renderTermsSheet(inDrawer);
    }

    // Keep the drawer's accept CTA and status in sync with the submit lifecycle:
    // the CTA locks while the request is in flight, the status line shows the
    // in-flight and error states (the latter only while the drawer owns submit).
    renderTermsSheet(inDrawer) {
      if (!this.termsButton) return;
      const mustScroll = !this.termsScrolledToEnd;
      this.termsButton.disabled = this.isSubmitting || this.submitState === 'success' || mustScroll;

      if (!this.termsStatus) return;
      if (this.submitState === 'error' && inDrawer) {
        this.termsStatus.textContent = this.submitMessage;
        this.termsStatus.classList.add('is-error');
        this.termsStatus.hidden = !this.submitMessage;
      } else if (mustScroll && inDrawer) {
        // Tell the user why the CTA is locked (a11y: disabled with no reason).
        this.termsStatus.textContent = this.messages.termsScrollHint || '';
        this.termsStatus.classList.remove('is-error');
        this.termsStatus.hidden = !this.termsStatus.textContent;
      } else {
        // Keep the last text so it can fade out; CSS animates the collapse.
        this.termsStatus.classList.remove('is-error');
        this.termsStatus.hidden = true;
      }
      // [hidden] is animated (kept in flow), so mirror it to the a11y tree.
      this.termsStatus.setAttribute('aria-hidden', this.termsStatus.hidden ? 'true' : 'false');
    }

    /* ── Submit ── */

    // `inline` marks errors whose field already owns visible inline UI, so the
    // status area stays empty and the inline message speaks for them. Errors
    // without inline UI (or with a hidden field) carry the message to the status.
    getFirstValidationError() {
      if (!this.canShowResult()) {
        return { field: 'details', message: this.messages.completeSteps, inline: false };
      }
      if (!this.endpoint) {
        return { field: 'endpoint', message: this.messages.endpointUnavailable, inline: false };
      }
      // contact.first_name is required by the intake schema (400 otherwise).
      // Validated even when the field is hidden, same as the email below.
      if (!this.isValidFirstName()) {
        return { field: 'firstName', message: this.messages.invalidFirstName, inline: Boolean(this.resultPanel?.hasFirstName()) };
      }
      // Validate the resolved email even when the field is hidden (logged-in
      // customers render with hide_email). A phone-only Shopify account has an
      // empty customer.email, so without this guard contact.email is POSTed blank.
      // The inline email error only exists when the field is rendered.
      if (!this.isValidEmail()) {
        return { field: 'email', message: this.messages.invalidEmail, inline: Boolean(this.resultPanel?.hasEmail()) };
      }
      // The phone field is always rendered, so its inline error always speaks
      // for it — same shape as consent, no status message needed.
      if (this.phoneError()) {
        return { field: 'phone', inline: true };
      }
      if (this.resultPanel?.hasConsent() && !this.isConsentSatisfied()) {
        return { field: 'consent', inline: true };
      }
      return null;
    }

    // The details step owns its own fields; the panel focuses name/email/consent
    // (and the status area for the endpoint error), the host only the details
    // container itself when the steps aren't complete.
    focusError(field) {
      if (field === 'details') {
        const container = this.stepContainers.get('details');
        if (container && typeof container.focus === 'function') {
          window.requestAnimationFrame(() => {
            container.focus({ preventScroll: true });
          });
        }
        return;
      }
      this.resultPanel?.focusField(field);
    }

    getFlowKey() {
      const hasReturns = this.hasReturnsSelected();
      const hasSell = this.hasSellDataComplete();
      if (hasReturns && hasSell) return 'combined';
      if (hasReturns) return 'order-returns';
      return 'sell-clothes';
    }

    buildTrackingContext() {
      const eventId = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `rendi-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const attributionContext = window.RendiAttribution?.getContext?.() || {};
      const tidFromCookie = attributionContext.marketing_attribution?.last_touch?.params?.tid || null;
      const tidFromUrl = new URLSearchParams(location.search).get('tid');

      return {
        eventId,
        eventName: 'typeform_submit',
        formId: 'PXVrWgJR',
        platformIdentifiers: attributionContext.platform_identifiers || {},
        tid: tidFromCookie || tidFromUrl || null,
        marketingAttribution: window.RendiAttribution?.getSubmissionAttribution?.() || null
      };
    }

    getPayload(trackingContext) {
      const timestamp = new Date().toISOString();
      const hasReturns = this.hasReturnsSelected();
      const hasSell = this.hasSellDataComplete();
      const phone = this.getNormalizedPhone();

      const payload = {
        paperless: true,
        source: 'rendi-form',
        schema_version: this.schemaVersion,
        page_url: window.location.href,
        estimate: {
          value_cents: this.calculateEstimate(),
          currency: 'EUR'
        },
        contact: {
          email: this.getResolvedEmail(),
          first_name: this.getResolvedFirstName(),
          country: this.country
        },
        tracking: {
          event_id: trackingContext.eventId,
          event_name: trackingContext.eventName,
          form_id: trackingContext.formId,
          fbp: trackingContext.platformIdentifiers?.meta?.fbp || null,
          fbc: trackingContext.platformIdentifiers?.meta?.fbc || null,
          tid: trackingContext.tid || null
        },
        consent: {
          accepted: this.isConsentSatisfied(),
          privacy_url: this.privacyUrl || null,
          accepted_at: this.isConsentSatisfied() ? timestamp : null
        },
        submitted_at: timestamp
      };

      // Optional field: omitted when empty so a submission without a phone
      // never clears a value already stored server-side.
      if (phone) {
        payload.contact.phone = phone;
      }

      // Only under marketing consent and within the landing window.
      if (trackingContext.marketingAttribution) {
        payload.tracking.marketing_attribution = trackingContext.marketingAttribution;
      }

      if (hasSell) {
        payload.answers = {
          brand: this.state.brand,
          quantity: Number(this.state.quantity),
          age: this.state.age,
          condition: this.state.condition,
          washed: this.state.washed,
          folded: this.state.folded
        };
        // The counts are the customer's answer, sent whatever the bonus shows.
        if (FEATURED_ENABLED && featuredTotal(this.state.featuredCounts) > 0) {
          payload.answers.featured_counts = Object.fromEntries(
            Object.entries(this.state.featuredCounts).filter(([, count]) => count > 0)
          );
        }
      }

      if (hasReturns) {
        payload.returns = this.state.returns;
      }

      if (hasReturns && hasSell) {
        payload.estimate.breakdown = {
          sell_cents: this.calculateSellEstimate(),
          returns_cents: this.calculateReturnsCashback()
        };
      }

      return payload;
    }

    pushTrackingEvents(trackingContext) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'RendiForm',
        event_id: trackingContext.eventId,
        event_name: trackingContext.eventName,
        form_id: trackingContext.formId
      });
    }

    trackEvent(eventName, data = {}) {
      // rendi-analytics.js owns the transport (dataLayer + queued PostHog capture)
      // and is loaded on this layout too. Optional-call so a load failure is a no-op.
      window.rendiTrack?.(eventName, data);
    }

    // Map a submit failure to its user message and tracking type in one place, so
    // the inline status text and the analytics event can never classify the same
    // error differently.
    classifyError(error) {
      if (error.name === 'AbortError') {
        return { message: this.messages.timeout, type: 'timeout' };
      }
      if (error instanceof SyntaxError) {
        return { message: this.messages.serverInvalid, type: 'server_invalid' };
      }
      return { message: error.message || this.messages.temporaryError, type: 'server_error' };
    }

    // The Conferma button on the details step: validate the contact fields,
    // then either force-open the pickup-terms drawer (sell flows: the accept
    // CTA inside it triggers the POST) or POST directly (returns-only, no
    // terms). The drawer reopens un-accepted on EVERY Conferma tap: accepting
    // the terms is a per-attempt act, never a persisted flag.
    confirm() {
      if (this.isSubmitting) return;
      if (this.submitState === 'success') return;
      // Only the details step confirms; ignore implicit form submission
      // (e.g. Enter pressed in the quantity field) on earlier steps.
      if (this.getCurrentStep() !== 'details') return;

      this.hasAttemptedSubmit = true;

      const firstError = this.getFirstValidationError();
      if (firstError) {
        this.submitState = firstError.inline ? 'idle' : 'error';
        this.submitMessage = firstError.inline ? '' : firstError.message;
        this.render();
        this.focusError(firstError.field);
        return;
      }

      // Clear any stale error from a previous attempt before the new one.
      this.submitState = 'idle';
      this.submitMessage = '';

      if (this.termsRequired()) {
        this.resetTerms();
        // Re-arm the read-gate for this open; syncTermsScrollGate settles it
        // once the drawer has laid out.
        this.termsScrolledToEnd = false;
        this.render();
        // Optional-call guard: if the sheet element failed to upgrade, the
        // drawer can't gate the submit and the flow simply stalls here.
        this.termsSheet?.open?.();
        if (this.termsBody) this.termsBody.scrollTop = 0;
        this.syncTermsScrollGate();
        return;
      }

      this.submit();
    }

    async submit() {
      // Ignore re-entrant calls while a request is in flight (e.g. the accept
      // CTA tapped again before the disable paints).
      if (this.isSubmitting) return;

      // A completed submit is terminal: navigating back to the details step
      // must not re-POST and create a duplicate lead.
      if (this.submitState === 'success') return;

      if (this.getCurrentStep() !== 'details') return;

      this.isSubmitting = true;
      this.phoneRefused = false;
      this.submitState = 'submitting';
      this.submitMessage = '';
      this.render();

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 10000);

      try {
        const trackingContext = this.buildTrackingContext();
        const response = await window.fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.getPayload(trackingContext)),
          signal: controller.signal
        });

        const rawText = await response.text();
        let data;
        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch {
          throw new SyntaxError(this.messages.serverInvalid);
        }

        if (response.status === 400 && Array.isArray(data.details) &&
            data.details.some((detail) => detail?.instancePath === '/contact/phone')) {
          this.phoneRefused = true;
          try {
            this.trackEvent('rendi_form_submit_error', { error_type: 'phone_refused' });
          } catch { /* non-critical */ }
          this.submitState = 'idle';
          if (this.termsRequired()) this.resetTerms();
          this.termsSheet?.close?.();
          return;
        }

        if (!response.ok || !data.accepted) {
          throw new Error(data.message || this.messages.temporaryError);
        }

        this.submitState = 'success';
        this.submitMessage = data.message || this.messages.success;
        this.hasAttemptedSubmit = false;
        this.clearStoredState();
        // Analytics must never turn a successful submit into an error: a throw here
        // would fall through to catch, flip the UI to error and prompt a duplicate
        // submission. Commit the success state first, then track non-critically.
        try {
          this.pushTrackingEvents(trackingContext);
          // Before the success event, so it lands on the person who submitted.
          window.rendiIdentify?.(this.getResolvedEmail());
          this.trackEvent('rendi_form_submit_success', {
            flow: this.getFlowKey(),
            estimate: this.estimateValueEuros(),
            has_phone: Boolean(this.getNormalizedPhone())
          });
          // Klar MMM custom conversion 1 ("rendi form completed", named in Klar's
          // Store Configurator): only submissions that contain a clothes sale.
          if (this.hasSellDataComplete()) {
            window.Shopify?.analytics?.publish?.('Klar:customConversionEvent', {
              customConversionId: 1,
              customConversionValue: 0
            });
          }
        } catch { /* non-critical */ }
      } catch (error) {
        const { message, type } = this.classifyError(error);
        this.submitState = 'error';
        this.submitMessage = message;
        // In the drawer flow, re-arm the acceptance so tapping the CTA retries.
        if (this.termsRequired()) this.resetTerms();
        this.trackEvent('rendi_form_submit_error', { error_type: type });
      } finally {
        window.clearTimeout(timeout);
        this.isSubmitting = false;

        // A successful submit closes the terms drawer (if open) and advances to
        // the terminal confirmation step; an error keeps the user on the
        // details step, with the message in the drawer or inline.
        if (this.submitState === 'success') {
          this.termsSheet?.close?.();
          const confirmationIndex = this.getSteps().indexOf('confirmation');
          if (confirmationIndex !== -1) {
            this.navigateTo(confirmationIndex, { push: true });
          } else {
            this.render();
          }
        } else {
          this.render();
          if (this.phoneRefused) this.focusError('phone');
        }
      }
    }
  }

  customElements.define('rendi-sell-form', RendiSellForm);
}
